import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import { enUS } from './locales/en-US'
import { ptBR } from './locales/pt-BR'

export const defaultLanguage = 'pt-BR'
export const supportedLanguages = [defaultLanguage, 'en-US'] as const

export type SupportedLanguage = (typeof supportedLanguages)[number]

/** Chave de persistencia do idioma escolhido pelo usuario (localStorage). */
const LANGUAGE_STORAGE_KEY = 'codout.language'

/** Narrowing seguro de `string` para os idiomas suportados (usado na leitura do storage e na UI). */
export function isSupportedLanguage(value: string): value is SupportedLanguage {
  return (supportedLanguages as readonly string[]).includes(value)
}

/**
 * Le o idioma persistido do localStorage. Cai no padrao (pt-BR) quando nada foi salvo, o valor e
 * invalido, ou o localStorage esta indisponivel (aba anonima/ambiente sem storage). Nunca lanca.
 */
function readStoredLanguage(): SupportedLanguage {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY)
    if (stored !== null && isSupportedLanguage(stored)) {
      return stored
    }
  } catch {
    // localStorage indisponivel: segue com o idioma padrao.
  }
  return defaultLanguage
}

/** Persiste a escolha de idioma; ignora falha de storage (a troca em memoria continua valendo). */
function persistLanguage(language: string): void {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  } catch {
    // Sem persistencia neste ambiente, mas a interface ja refletiu a troca.
  }
}

await i18n.use(initReactI18next).init({
  resources: {
    'pt-BR': ptBR,
    'en-US': enUS,
  },
  // Idioma inicial vem da escolha persistida do usuario; sem escolha previa, o padrao pt-BR.
  lng: readStoredLanguage(),
  fallbackLng: defaultLanguage,
  defaultNS: 'common',
  ns: ['common'],
  interpolation: {
    // React ja faz escaping.
    escapeValue: false,
  },
})

// Toda troca de idioma (via i18n.changeLanguage) fica persistida para sobreviver ao reload.
i18n.on('languageChanged', (language) => {
  if (isSupportedLanguage(language)) {
    persistLanguage(language)
  }
})

export default i18n
